/**
 * Represents the characteristics shared by all intensive properties of a
 * chemical Species.
 *
 * Uses the following files
 * 		SpecieState.js
 *
 * Variables
 * 		id								{number}		unique identifier to this particular species
 *
 * 		name							{string}		name, <sub/sup> tags permitted
 *
 * 		enthalpy						{number}		enthalpy of formation in kJ/mol
 *
 * 		entropy							{number}		standard state entropy in J/(K*mol)
 *
 * 		cp								{number}		heat capacity in cal/(K*g)
 *
 * 		state							{string}		state == {aq, s, l, g}
 *
 * 		charge							{number}		charge of the species, eg "-4"
 *
 * 		molecularWeight					{number}		weight of the species in g/mol
 *
 * 		density							{number}		density of the species in g/ml
 *
 * 		hue								{number}		H (of HSV) value of the species. 0 for transparent.
 *
 * 		saturation						{number}		S (of HSV) value of the species. 0 for transparent.
 *
 * 		value							{number}		V (of HSV) value of the species. 0 for transparent.
 *
 * 		standardColorConcentration		{number}		concentration of the species
 *
 * 		waterReplacement				{number}		molarity of water replaced by 1M of this specie
 *
 * 		unitActivityString				{string}		whether the species should be considered with activity 1
 *
 * 		casRN							{string}		CAS-RN for this specie
 */
define([
    './Constants',
    './SpecieState',
    './BandNode'
], function (Constants, SpecieState, BandNode) {

    function Species(s1, waterFinite) {
        this.id = s1.id || 0;
        this.name = s1.name || 'speciesName';

        this.enthalpy = s1.enthalpy || 0;
        this.entropy = s1.entropy || 0;

        this.cp = 0.0;
        if (s1.cp) {
            this.cp = s1.cp;
        } else if (s1.specificHeat) {
            this.cp = s1.specificHeat;
        }

        this.charge = s1.charge || 0.0;
        this.state = s1.state || 'aq';
        this.molecularWeight = s1.molecularWeight || 100.0;
        this.density = s1.density || 0.0;
        this.hue = s1.hue || 0;
        this.saturation = s1.saturation || 0;
        this.value = s1.value || 0;
        this.standardColorConcentration = s1.colorConcentration || 0.0;
        this.waterReplacement = s1.waterReplacement || 0.0;
        this.casRN = s1.casRN || 0;
        this.conductivity = s1.conductivity || 0.0; // Units are mS/cm / mol/L
        /// id of 0 reserved for solvent
        if (this.id === 0) {
            this.unitActivity = true;
            this.infinite = !waterFinite;
        } else {
            this.infinite = false;
            // Should probably put pure liquid here too!
            if (this.state === 's') {
                this.unitActivity = true;
            } else {
                this.unitActivity = false;
            }
            //return this;
        }
        this.unknown = s1.unknown || false;
        this.abs = null;
        this.bands = [];
    };

    Species.prototype.addBand = function (wavelength, width, e) {
        this.bands.push(new BandNode(wavelength, width, e));
    };

    /**
     * Returns a copy of the current species, not a reference
     * @returns {Species}
     */
    Species.prototype.clone = function () {
        return new Species(this);
    };

    /**
     * Returns true when compared to another species with the same id
     * @param other
     * @returns {boolean}
     */
    Species.prototype.equals = function (other) {
        if (other instanceof Species) {
            return (this.id === other.id) ? true : false;
        }
        else if (other.archetype) {
            return (this.id === other.archetype.id) ? true : false;
        }

        return false;
    };

    /**
     * Returns the density of the species. If no density, assume water (for
     * liquids) or solid's default.
     * @returns {number}
     */
    Species.prototype.getDensity = function () {
        // No const keyword in javascript, so we use closure
        // Create an instance of the class to access its constants

        if (this.density <= 0) {
            if (this.state === SpecieState.SOLID) {
                return Constants.SOLID_DENSITY;
            }
            else if (this.state === SpecieState.LIQUID) {
                return Constants.DENSITY_OF_WATER;
            }
        }

        return this.density;
    };

    Species.prototype.calculateAbsSpectrum = function () {
        this.abs = Array.apply(null, Array(Constants.MAX_WAVELENGTH - Constants.MIN_WAVELENGTH + 1)).map(Number.prototype.valueOf,0);
        if (this.bands.length > 0) {
            var wavelength;
            for (wavelength = Constants.MIN_WAVELENGTH; wavelength <= Constants.MAX_WAVELENGTH; wavelength++) {
                var i = wavelength - Constants.MIN_WAVELENGTH;
                this.abs[i] = 0;
                var j;
                for (j = 0; j < this.bands.length; j++) {
                    var w1 = this.bands[j].wavelength;
                    var a10 = this.bands[j].width;
                    var ma = this.bands[j].e;

                    this.abs[i] += ma * Math.pow(0.1, 4 * Math.pow((wavelength - w1) / a10, 2));
                }
            }
        }
    };

    Species.prototype.getAbsSpectrum = function () {
        if (this.abs === null && this.bands.length > 0) {
            this.calculateAbsSpectrum();
        }
        return this.abs;
    };

    return Species;
});
